<script>
import { formatPhoneNumber } from '@utils/phone-format'

export default {
  props: {
    connections: {
      type: Array,
      required: true,
    },
    groups: {
      type: Array,
      required: true,
    },
    hardwares: {
      type: Array,
      default: () => {
        return []
      },
      required: false,
    },
  },
  data() {
    return {
      searchText: '',
      searchInput: null,
      searchedColumn: '',
      formatPhoneNumber: formatPhoneNumber,
      columns: [
        {
          title: 'IMSI',
          dataIndex: 'imsi',
          key: 'imsi',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.sim.localeCompare(b.imsi),
        },
        {
          title: 'MTN #',
          dataIndex: 'sim',
          key: 'sim',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.sim.localeCompare(b.sim),
        },
        {
          title: 'Hardware',
          dataIndex: 'hardwareId',
          key: 'hardwareId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.hardwareId.localeCompare(b.hardwareId),
          filters: [
            {
              text: 'MD',
              value: 'md',
            },
            {
              text: 'CA',
              value: 'ca',
            },
            {
              text: 'LA',
              value: 'la',
            },
            {
              text: 'VA',
              value: 'va',
            },
          ],
          // specify the condition of filtering result
          // here is that finding the name started with `value`
          onFilter: (value, record) => record.hardwareId.indexOf(value) === 0,
        },
        {
          title: 'Connection ID',
          dataIndex: 'id',
          key: 'id',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.id - b.id,
        },
        {
          title: 'Locked',
          dataIndex: 'locked',
          key: 'locked',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
        {
          title: 'Loading',
          dataIndex: 'loading',
          key: 'loading',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
        {
          title: 'Actions',
          key: 'actions',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
      ],
    }
  },
  methods: {
    getGroupColor(id) {
      if (id && this.groups) {
        const group = this?.groups.filter((g) => g.sk === id)[0]
        const color = group?.meta?.color
        if (color) {
          return color
        } else {
          return 'pink'
        }
      } else {
        return 'pink'
      }
    },
    selectRow(record) {
      const connectionId = record.pk.replace('CONNECTION#', '')
      this.$router.push({
        name: 'connection-profile',
        params: { connectionId },
      })
    },
    onDelete(record) {
      this.$emit('deleteConnection', record)
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm()
      this.searchText = selectedKeys[0]
      this.searchedColumn = dataIndex
    },

    handleReset(clearFilters) {
      clearFilters()
      this.searchText = ''
    },
  },
}
</script>

<template>
  <a-table :data-source="connections" :columns="columns">
    <div
      slot="filterDropdown"
      slot-scope="{
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        column,
      }"
      style="padding: 8px"
    >
      <a-input
        v-ant-ref="(c) => (searchInput = c)"
        :placeholder="`Search ${column.dataIndex}`"
        :value="selectedKeys[0]"
        style="display: block; width: 188px; margin-bottom: 8px"
        @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
        @pressEnter="
          () => handleSearch(selectedKeys, confirm, column.dataIndex)
        "
      />
      <a-button
        type="primary"
        icon="search"
        size="small"
        style="width: 90px; margin-right: 8px"
        @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
      >
        Search
      </a-button>
      <a-button
        size="small"
        style="width: 90px"
        @click="() => handleReset(clearFilters)"
      >
        Reset
      </a-button>
    </div>
    <a-icon
      slot="filterIcon"
      slot-scope="filtered"
      type="search"
      :style="{ color: filtered ? '#108ee9' : undefined }"
    />
    <template slot="customRender" slot-scope="text, record, index, column">
      <span v-if="searchText && searchedColumn === column.dataIndex">
        <template
          v-for="(fragment, i) in text
            .toString()
            .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
        >
          <mark
            v-if="fragment.toLowerCase() === searchText.toLowerCase()"
            :key="i"
            class="highlight"
            >{{ fragment }}</mark
          >
          <template v-else>{{ fragment }}</template>
        </template>
      </span>
      <template v-else>
        <div
          v-if="column.key === 'imsi'"
          @click="() => selectRow(record, column)"
        >
          <a @click="() => selectRow(record, column)">{{
            record.imsi.toUpperCase()
          }}</a>
        </div>
        <div v-else-if="column.key === 'sim'">
          {{ formatPhoneNumber(text) }}
        </div>
        <div v-else-if="column.key === 'actions'">
          <ul class="actions">
            <li>
              <a
                href="javascript:;"
                title="Edit SIM"
                @click="() => selectRow(record, column)"
              >
                <a-button type="primary" icon="edit" size="small"></a-button>
              </a>
            </li>
            <li>
              <a-popconfirm
                title="Sure to delete?"
                @confirm="() => onDelete(record)"
              >
                <a href="javascript:;" title="Edit SIM">
                  <a-button type="danger" icon="delete" size="small"></a-button>
                </a>
              </a-popconfirm>
            </li>
          </ul>
        </div>
        <div v-else-if="column.key === 'locked'">
          <a-icon
            v-if="text"
            :theme="'twoTone'"
            :two-tone-color="'#f5222d'"
            type="lock"
          />
          <a-icon
            v-else
            :theme="'twoTone'"
            :two-tone-color="'#52c41a'"
            type="unlock"
          />
        </div>
        <div v-else-if="column.key === 'loading'">
          <span v-if="text"
            ><a-icon type="loading" :color="'green'"></a-icon
          ></span>
        </div>
        <div v-else>
          {{ text }}
        </div>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
.highlight {
  padding: 0;
  background-color: rgb(255, 192, 105);
}
#conn--container td > div {
  text-transform: uppercase;
}
</style>
